import type { ICSPRClickSDK } from '@make-software/csprclick-core-types';
import { ErrProcessHttpRequest, HttpError, HttpHandler, RpcClient, type IHandler, type RpcRequest, type RpcResponse } from 'casper-js-sdk';

/**
 * Every Casper node read the browser makes goes through CSPR.click's node proxy
 * (`getCsprCloudProxy()`: `RpcURL`, authorised by `RpcDigestToken`). Both are read for each request:
 * the SDK loads asynchronously and the token can change. There is no other endpoint: without
 * CSPR.click the read fails; it never falls back to a fixed URL.
 *
 * Only reads go through here. Transactions are submitted by CSPR.click (`clickRef.send`), so a read
 * that the proxy refuses for load (429, 502, 503, 504) or that fails in transit is repeated with a
 * short, bounded backoff, and anything that is not a known read is sent once and never repeated.
 */

export type CasperRpcFailure = 'busy' | 'unreachable' | 'not-ready' | 'wrong-chain';

/** A node read that could not be made, so nothing was built, signed or sent because of it. */
export class CasperRpcError extends Error {
  constructor(readonly kind: CasperRpcFailure, message: string, readonly status?: number) {
    super(message);
    this.name = 'CasperRpcError';
  }
}

/** A JSON-RPC error answer from the node (not a transport failure; never repeated). */
export class CasperNodeError extends Error {
  constructor(readonly code: number, message: string) {
    super(message);
    this.name = 'CasperNodeError';
  }
}

/** The JSON-RPC reads this app makes. Anything else (a submission) is never repeated. */
export const CASPER_READ_METHODS: ReadonlySet<string> = new Set([
  'info_get_status',
  'info_get_transaction',
  'info_get_deploy',
  'info_get_chainspec',
  'chain_get_state_root_hash',
  'chain_get_block',
  'query_global_state',
  'state_get_item',
  'state_get_dictionary_item',
  'query_balance',
]);

/** Statuses a proxy answers while it is briefly overloaded. */
const TRANSIENT_STATUS = new Set([429, 502, 503, 504]);

export interface RetryPolicy {
  /** The pause before each repeat (one repeat per entry). */
  delaysMs: readonly number[];
  /** Up to this much random time is added to each pause. */
  jitterMs: number;
  /** A read ends within this in all: every request, its answer and the pauses between them. */
  budgetMs: number;
}

export const READ_RETRY: RetryPolicy = { delaysMs: [500, 1000, 2000], jitterMs: 250, budgetMs: 8000 };

/** How long a read waits for CSPR.click to finish loading before it fails. */
export const CLICK_WAIT_MS = 5000;

let click: ICSPRClickSDK | null = null;
const clickWaiters = new Set<() => void>();

/** The loaded CSPR.click SDK (`useClickRef()` is set only after `csprclick:loaded`), or null. */
export function bindCsprClick(sdk: ICSPRClickSDK | null | undefined): void {
  click = sdk ?? null;
  if (!click) return;
  for (const wake of clickWaiters) wake();
  clickWaiters.clear();
}

const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

async function loadedClick(): Promise<ICSPRClickSDK | null> {
  if (click) return click;
  await new Promise<void>((resolve) => {
    const wake = () => {
      clearTimeout(timer);
      resolve();
    };
    const timer = setTimeout(() => {
      clickWaiters.delete(wake);
      resolve();
    }, CLICK_WAIT_MS);
    clickWaiters.add(wake);
  });

  return click;
}

const NOT_READY = 'CSPR.click has not finished loading, so the Casper network could not be read.';

/** CSPR.click's node proxy for this request: its URL and the token sent as `Authorization`. */
export async function casperRpcEndpoint(): Promise<{ url: string; token: string }> {
  const sdk = await loadedClick();
  if (!sdk) throw new CasperRpcError('not-ready', NOT_READY);
  let url: unknown;
  let token: unknown;
  try {
    const proxy = sdk.getCsprCloudProxy();
    url = proxy?.RpcURL;
    token = proxy?.RpcDigestToken;
  } catch {
    // not ready: reported below
  }
  if (typeof url !== 'string' || !url.startsWith('https://') || typeof token !== 'string' || token === '')
    throw new CasperRpcError('not-ready', NOT_READY);

  return { url, token };
}

/** Milliseconds from a `Retry-After` header (seconds or an HTTP date); null when absent or unreadable. */
export function retryAfterMs(header: string | null | undefined, now = Date.now()): number | null {
  if (!header) return null;
  const value = header.trim();
  if (/^\d+$/.test(value)) return Number(value) * 1000;
  const at = Date.parse(value);

  return Number.isNaN(at) ? null : Math.max(0, at - now);
}

/** Why one attempt failed: worth repeating (with the server's wish, if any), or not. */
type Attempt = { transient: false } | { transient: true; status?: number; retryAfter: number | null };

const EXPIRED = Symbol('read budget spent');

/**
 * One attempt, abandoned when `deadline` passes: its signal is aborted and it rejects with
 * `EXPIRED`. An answer that arrives later is dropped.
 */
async function beforeDeadline<T>(attempt: (signal: AbortSignal) => Promise<T>, deadline: number): Promise<T> {
  const controller = new AbortController();
  let timer: ReturnType<typeof setTimeout> | undefined;
  const expired = new Promise<never>((_, reject) => {
    timer = setTimeout(() => {
      controller.abort();
      reject(EXPIRED);
    }, Math.max(0, deadline - Date.now()));
  });
  try {
    return await Promise.race([attempt(controller.signal), expired]);
  } finally {
    clearTimeout(timer);
  }
}

const readFailure = (why: Attempt | null) =>
  why?.transient && why.status !== undefined
    ? new CasperRpcError('busy', `The Casper network service is busy (HTTP ${why.status}).`, why.status)
    : new CasperRpcError('unreachable', "The Casper network service couldn't be reached.");

/**
 * Runs `attempt`. A call that is not `repeatable` (anything but a known read) is made once, as it
 * is: never repeated and never cut short. A read ends within the policy budget in all (requests,
 * their answers and the pauses): a failure `classify` calls transient is repeated after the
 * policy's pauses (or the server's `Retry-After`) while the budget allows; one that stays
 * transient, or an attempt still unanswered when the budget is spent, becomes a `CasperRpcError`.
 * Any other failure is thrown as it is.
 */
export async function withReadRetry<T>(
  attempt: (signal?: AbortSignal) => Promise<T>,
  classify: (err: unknown) => Attempt,
  repeatable: boolean,
  policy: RetryPolicy = READ_RETRY,
  random: () => number = Math.random,
): Promise<T> {
  if (!repeatable) return attempt();
  const deadline = Date.now() + policy.budgetMs;
  let last: Attempt | null = null;
  for (let i = 0; ; i += 1) {
    try {
      return await beforeDeadline(attempt, deadline);
    } catch (err) {
      if (err === EXPIRED) throw readFailure(last);
      if (err instanceof CasperRpcError) throw err;
      const why = classify(err);
      if (!why.transient) throw err;
      last = why;
      const pause = why.retryAfter ?? policy.delaysMs[i] + Math.floor(random() * policy.jitterMs);
      if (i >= policy.delaysMs.length || Date.now() + pause >= deadline) throw readFailure(why);
      await sleep(pause);
    }
  }
}

class HttpStatusError extends Error {
  constructor(readonly status: number, readonly retryAfter: number | null) {
    super(`HTTP ${status}`);
  }
}

const classifyFetch = (err: unknown): Attempt =>
  err instanceof HttpStatusError
    ? TRANSIENT_STATUS.has(err.status) ? { transient: true, status: err.status, retryAfter: err.retryAfter } : { transient: false }
    : err instanceof TypeError // fetch rejects with a TypeError when the request got no answer
      ? { transient: true, retryAfter: null }
      : { transient: false };

/**
 * One JSON-RPC read through the proxy, repeated on load or transit failures. Only methods in
 * `CASPER_READ_METHODS` are accepted: this function never submits anything.
 */
export async function casperRpcRead<T = unknown>(method: string, params: unknown): Promise<T> {
  if (!CASPER_READ_METHODS.has(method)) throw new Error(`Not a Casper read: ${method}`);
  const body = JSON.stringify({ jsonrpc: '2.0', id: 1, method, params });
  const answer = await withReadRetry(async (signal) => {
    const { url, token } = await casperRpcEndpoint();
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: token },
      body,
      signal,
    });
    if (!response.ok) throw new HttpStatusError(response.status, retryAfterMs(response.headers.get('Retry-After')));

    return (await response.json()) as { result?: T; error?: { code?: number; message?: string; data?: unknown } };
  }, classifyFetch, true);
  if (answer?.error) {
    const detail = typeof answer.error.data === 'string' ? `: ${answer.error.data}` : '';
    throw new CasperNodeError(Number(answer.error.code ?? 0), `${answer.error.message ?? 'Node error'}${detail}`);
  }
  if (answer?.result === undefined) throw new CasperNodeError(0, 'The node answered without a result');

  return answer.result;
}

const classifySdk = (err: unknown): Attempt => {
  if (HttpError.isHttpError(err))
    return TRANSIENT_STATUS.has(err.statusCode) ? { transient: true, status: err.statusCode, retryAfter: null } : { transient: false };
  // The SDK's fetch client wraps every failure, an HTTP status included, as
  // "failed to send http request, details: <cause>" (`ErrProcessHttpRequest`).
  const message = err instanceof Error ? err.message : '';
  if (!message.startsWith(`${ErrProcessHttpRequest.message}, details:`)) return { transient: false };
  const status = /\bCode: (\d{3})\b/.exec(message);
  if (!status) return { transient: true, retryAfter: null };

  return TRANSIENT_STATUS.has(Number(status[1])) ? { transient: true, status: Number(status[1]), retryAfter: null } : { transient: false };
};

/**
 * The `casper-js-sdk` handler: each call goes to CSPR.click's proxy as it is at that moment. Reads
 * are repeated as `casperRpcRead` repeats them; any other method is sent once.
 */
export class CsprClickRpcHandler implements IHandler {
  async processCall(request: RpcRequest): Promise<RpcResponse> {
    return withReadRetry(async () => {
      const { url, token } = await casperRpcEndpoint();
      const handler = new HttpHandler(url, 'fetch');
      handler.setCustomHeaders({ Authorization: token });

      return handler.processCall(request);
    }, classifySdk, CASPER_READ_METHODS.has(String(request.method)));
  }
}

/** The SDK client (transaction building needs one); every call goes through `CsprClickRpcHandler`. */
export const sharedRpcClient = new RpcClient(new CsprClickRpcHandler());

export interface CasperNodeStatus {
  chainspecName: string;
  /** 2 for a Casper 2.x node, else 1. */
  apiVersion: 1 | 2;
}

const statusByUrl = new Map<string, Promise<CasperNodeStatus>>();

/** The proxy's node status, read once per session (per proxy URL); a failed read is not kept. */
export async function casperNodeStatus(): Promise<CasperNodeStatus> {
  const { url } = await casperRpcEndpoint();
  let status = statusByUrl.get(url);
  if (!status) {
    const read = casperRpcRead<{ chainspec_name?: unknown; api_version?: unknown }>('info_get_status', []).then(
      (s): CasperNodeStatus => {
        if (typeof s?.chainspec_name !== 'string' || s.chainspec_name === '' || typeof s.api_version !== 'string')
          throw new CasperNodeError(0, 'The node status is malformed');

        return { chainspecName: s.chainspec_name, apiVersion: s.api_version.startsWith('2.') ? 2 : 1 };
      },
    );
    statusByUrl.set(url, read);
    read.catch(() => {
      if (statusByUrl.get(url) === read) statusByUrl.delete(url);
    });
    status = read;
  }

  return status;
}

/**
 * The node status, only when the proxy serves the chain this build signs for
 * (`config.cspr_chain_name`): a mainnet build on a testnet proxy, or the reverse, builds nothing.
 */
export async function requireCasperChain(): Promise<CasperNodeStatus> {
  const status = await casperNodeStatus();
  if (status.chainspecName !== config.cspr_chain_name)
    throw new CasperRpcError(
      'wrong-chain',
      `CSPR.click serves the Casper network "${status.chainspecName}", but this app is set up for "${String(config.cspr_chain_name)}".`,
    );

  return status;
}

/** Tests only: forget the loaded SDK and the cached node status. */
export function resetCasperRpcForTests(): void {
  click = null;
  clickWaiters.clear();
  statusByUrl.clear();
}
